import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../services/authService';
import { TimeLogsService, TimeLog, CreateTimeLogData } from '../services/timelogsService';
import { goalService } from '../services/goalService';
import { habitService, Habit } from '../services/habitService';
import { taskService, Task } from '../services/taskService';
import './Logs.css';

interface LogsProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

// TimeLog interface is now imported from timelogsService

const Logs: React.FC<LogsProps> = ({ user, onClose, onToggleSidebar }) => {
  const navigate = useNavigate();
  const [timeLogs, setTimeLogs] = useState<TimeLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddLogModal, setShowAddLogModal] = useState(false);
  const [showStatsSection, setShowStatsSection] = useState(true);
  const [showLogsSection, setShowLogsSection] = useState(true);
  const [filterType, setFilterType] = useState<'all' | 'goal' | 'task' | 'habit' | 'meditation'>('all');
  const [filterDate, setFilterDate] = useState('');
  
  const [manualLog, setManualLog] = useState({
    activityType: 'goal' as 'goal' | 'task' | 'habit' | 'meditation',
    activityId: '',
    activityName: '',
    duration: '' as number | string,
    description: '',
    date: new Date().toISOString().split('T')[0]
  });

  // State for available activities
  const [availableGoals, setAvailableGoals] = useState<any[]>([]);
  const [availableHabits, setAvailableHabits] = useState<Habit[]>([]);
  const [availableTasks, setAvailableTasks] = useState<Task[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  
  // State for autocomplete
  const [activitySearchQuery, setActivitySearchQuery] = useState('');
  const [showActivityDropdown, setShowActivityDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Load time logs on component mount
  useEffect(() => {
    if (user?.id) {
      loadTimeLogs();
    }
  }, [user?.id]);

  // Load time logs when filters change
  useEffect(() => {
    if (user?.id) {
      loadTimeLogs();
    }
  }, [filterType, filterDate, user?.id]);

  const loadTimeLogs = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const params: any = {
        limit: 100
      };
      
      if (filterType !== 'all') {
        params.activity_type = filterType;
      }
      
      if (filterDate) {
        params.start_date = filterDate;
        params.end_date = filterDate;
      }
      
      const response = await TimeLogsService.getUserTimeLogs(user.id, params);
      setTimeLogs(response.data);
    } catch (err: any) {
      console.error('Error loading time logs:', err);
      setError(err.message || 'Failed to load time logs');
    } finally {
      setLoading(false);
    }
  };

  const loadActivities = async () => {
    if (!user?.id) return;
    
    try {
      setLoadingActivities(true);
      
      // Load goals
      const goalsResponse = await goalService.getUserGoals({
        user_id: user.id.toString(),
        limit: 100
      });
      if (goalsResponse.success && goalsResponse.goals) {
        setAvailableGoals(goalsResponse.goals);
      }
      
      // Load habits
      const habitsResponse = await habitService.getUserHabits(parseInt(user.id.toString()), {
        is_active: true,
        limit: 100
      });
      if (habitsResponse.success && habitsResponse.habits) {
        setAvailableHabits(habitsResponse.habits);
      }
      
      // Load tasks
      const tasksResponse = await taskService.getUserTasks({
        user_id: user.id.toString(),
        limit: 100
      });
      if (tasksResponse.success && tasksResponse.tasks) {
        setAvailableTasks(tasksResponse.tasks);
      }
    } catch (err: any) {
      console.error('Error loading activities:', err);
      setError(err.message || 'Failed to load activities');
    } finally {
      setLoadingActivities(false);
    }
  };

  const formatTime = (milliseconds: number): string => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleActivitySelection = (activityId: string, activityName: string) => {
    setManualLog(prev => ({
      ...prev,
      activityId,
      activityName
    }));
  };

  const getAvailableActivities = () => {
    switch (manualLog.activityType) {
      case 'goal':
        return availableGoals;
      case 'habit':
        return availableHabits;
      case 'task':
        return availableTasks;
      default:
        return [];
    }
  };

  const getFilteredActivities = () => {
    const activities = getAvailableActivities();
    if (!activitySearchQuery.trim()) {
      return activities;
    }
    const query = activitySearchQuery.toLowerCase();
    return activities.filter(activity => {
      const activityName = manualLog.activityType === 'goal' 
        ? activity.title 
        : activity.name || activity.title;
      return activityName.toLowerCase().includes(query);
    });
  };

  const handleActivityInputChange = (value: string) => {
    setActivitySearchQuery(value);
    setShowActivityDropdown(true);
    setHighlightedIndex(-1);
    
    // If user clears the input, also clear the selected activity
    if (!value.trim()) {
      setManualLog(prev => ({ ...prev, activityId: '', activityName: '' }));
    }
  };

  const handleActivitySelect = (activityId: string, activityName: string) => {
    handleActivitySelection(activityId, activityName);
    setActivitySearchQuery(activityName);
    setShowActivityDropdown(false);
    setHighlightedIndex(-1);
  };

  const handleActivityInputFocus = () => {
    setShowActivityDropdown(true);
  };

  const handleActivityInputBlur = () => {
    // Delay hiding dropdown to allow click events to fire
    setTimeout(() => {
      setShowActivityDropdown(false);
      setHighlightedIndex(-1);
    }, 200);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const filtered = getFilteredActivities();
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setShowActivityDropdown(true);
      setHighlightedIndex(prev => 
        prev < filtered.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter' && highlightedIndex >= 0 && highlightedIndex < filtered.length) {
      e.preventDefault();
      const activity = filtered[highlightedIndex];
      const activityId = activity.id.toString();
      const activityName = manualLog.activityType === 'goal' 
        ? activity.title 
        : activity.name || activity.title;
      handleActivitySelect(activityId, activityName);
    } else if (e.key === 'Escape') {
      setShowActivityDropdown(false);
      setHighlightedIndex(-1);
    }
  };

  const addManualLog = async () => {
    const duration = typeof manualLog.duration === 'string' ? parseInt(manualLog.duration) : manualLog.duration;
    
    if (!user?.id || !manualLog.activityName || !duration || duration <= 0) {
      setError('Please fill in all required fields');
      return;
    }
    
    if (duration > 1440) {
      setError('Duration cannot exceed 1440 minutes (24 hours)');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Convert duration to number
      const duration = typeof manualLog.duration === 'string' ? parseInt(manualLog.duration, 10) : manualLog.duration;
      
      // Create start and end times for the specified date
      const startTime = new Date(`${manualLog.date}T09:00:00`).toISOString();
      
      // Calculate end time by adding duration to start time
      const startDateTime = new Date(startTime);
      const endDateTime = new Date(startDateTime.getTime() + duration * 60 * 1000);
      const endTime = endDateTime.toISOString();
      
      // Validate the calculated times
      if (isNaN(startDateTime.getTime()) || isNaN(endDateTime.getTime())) {
        throw new Error('Invalid date calculation');
      }
      
      const timeLogData: CreateTimeLogData = {
        user_id: parseInt(user.id.toString()),
        activity_type: manualLog.activityType,
        activity_id: manualLog.activityId || `manual_${Date.now()}`,
        activity_name: manualLog.activityName,
        duration_minutes: duration,
        start_time: startTime,
        end_time: endTime,
        description: manualLog.description || undefined,
        is_manual_entry: true
      };
      
      const newLog = await TimeLogsService.createTimeLog(timeLogData);
      
      // Reload time logs to get the latest data
      await loadTimeLogs();
      
      setShowAddLogModal(false);
      setManualLog({
        activityType: 'goal',
        activityId: '',
        activityName: '',
        duration: '',
        description: '',
        date: new Date().toISOString().split('T')[0]
      });
      setActivitySearchQuery('');
      setShowActivityDropdown(false);
      setHighlightedIndex(-1);
    } catch (err: any) {
      console.error('Error adding manual log:', err);
      setError(err.message || 'Failed to add time log');
    } finally {
      setLoading(false);
    }
  };

  const getTotalTimeToday = (): number => {
    const today = new Date().toISOString().split('T')[0];
    return timeLogs
      .filter(log => new Date(log.start_time).toISOString().split('T')[0] === today)
      .reduce((total, log) => total + log.duration_minutes, 0);
  };

  const getTotalTimeThisWeek = (): number => {
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return timeLogs
      .filter(log => new Date(log.start_time) >= weekAgo)
      .reduce((total, log) => total + log.duration_minutes, 0);
  };

  const getTotalTimeThisMonth = (): number => {
    const monthAgo = new Date();
    monthAgo.setMonth(monthAgo.getMonth() - 1);
    return timeLogs
      .filter(log => new Date(log.start_time) >= monthAgo)
      .reduce((total, log) => total + log.duration_minutes, 0);
  };

  const getFilteredLogs = (): TimeLog[] => {
    let filtered = timeLogs;
    
    if (filterType !== 'all') {
      filtered = filtered.filter(log => log.activity_type === filterType);
    }
    
    if (filterDate) {
      filtered = filtered.filter(log => new Date(log.start_time).toISOString().split('T')[0] === filterDate);
    }
    
    return filtered.sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime());
  };

  const getActivityTypeStats = () => {
    const stats = { goal: 0, task: 0, habit: 0, meditation: 0 };
    timeLogs.forEach(log => {
      stats[log.activity_type] += log.duration_minutes;
    });
    return stats;
  };

  return (
    <div className="logs-container">
      <div className="logs-header">
        <div className="header-left">
          <button className="menu-toggle" onClick={onToggleSidebar}>
            ☰
          </button>
          <h1>📋 /logs</h1>
        </div>
        <div className="header-right">
          <button className="timer-btn" onClick={() => navigate('/timer')}>⏱️</button>
        </div>
      </div>

      <div className="logs-content">
        {loading && (
          <div className="loading-message">
            Loading time logs...
          </div>
        )}
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* Stats Section */}
        <div className="section">
          <button 
            className="section-toggle" 
            onClick={() => setShowStatsSection(!showStatsSection)}
          >
            <span className="toggle-text">/stats</span>
            <span className="toggle-arrow">{showStatsSection ? '▼' : '▶'}</span>
          </button>
          <div className={`section-content ${showStatsSection ? 'expanded' : 'collapsed'}`}>
            <div className="stats-grid">
              <div className="stat-item">
                <div className="stat-label">Today</div>
                <div className="stat-value">{formatTime(getTotalTimeToday() * 60000)}</div>
              </div>
              <div className="stat-item">
                <div className="stat-label">This Week</div>
                <div className="stat-value">{formatTime(getTotalTimeThisWeek() * 60000)}</div>
              </div>
              <div className="stat-item">
                <div className="stat-label">This Month</div>
                <div className="stat-value">{formatTime(getTotalTimeThisMonth() * 60000)}</div>
              </div>
              <div className="stat-item">
                <div className="stat-label">Total Logs</div>
                <div className="stat-value">{timeLogs.length}</div>
              </div>
            </div>
            
            <div className="activity-stats">
              <h4>Time by Activity Type</h4>
              <div className="activity-stats-grid">
                {Object.entries(getActivityTypeStats()).map(([type, minutes]) => (
                  <div key={type} className="activity-stat-item">
                    <div className="activity-stat-label">{type}</div>
                    <div className="activity-stat-value">{formatTime(minutes * 60000)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Time Logs Section */}
        <div className="section">
          <button 
            className="section-toggle" 
            onClick={() => setShowLogsSection(!showLogsSection)}
          >
            <span className="toggle-text">/logs</span>
            <span className="toggle-arrow">{showLogsSection ? '▼' : '▶'}</span>
          </button>
          <div className={`section-content ${showLogsSection ? 'expanded' : 'collapsed'}`}>
            <div className="logs-header-controls">
              <div className="filters">
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value as any)}
                >
                  <option value="all">All Types</option>
                  <option value="goal">Goals</option>
                  <option value="task">Tasks</option>
                  <option value="habit">Habits</option>
                  <option value="meditation">Meditation</option>
                </select>
                
                <input
                  type="date"
                  value={filterDate}
                  onChange={(e) => setFilterDate(e.target.value)}
                  placeholder="Filter by date"
                />
              </div>
              
              <button 
                className="add-log-btn"
                onClick={() => {
                  setShowAddLogModal(true);
                  loadActivities();
                }}
              >
                + Add Log
              </button>
            </div>
            
            <div className="logs-list">
              {getFilteredLogs().length === 0 ? (
                <div className="empty-state">
                  <p>No time logs found. Add a log or start a timer.</p>
                </div>
              ) : (
                getFilteredLogs().map(log => (
                  <div key={log.timelog_id} className="log-item">
                    <div className="log-header">
                      <span className="log-activity">{log.activity_name}</span>
                      <span className="log-duration">{formatTime(log.duration_minutes * 60000)}</span>
                    </div>
                    <div className="log-details">
                      <span className="log-type">{log.activity_type}</span>
                      <span className="log-date">
                        {new Date(log.start_time).toLocaleDateString()}
                      </span>
                      <span className="log-time">
                        {new Date(log.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    {log.description && (
                      <div className="log-description">{log.description}</div>
                    )}
                    {log.is_manual_entry && (
                      <div className="log-manual-indicator">Manual Entry</div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Manual Log Modal */}
      {showAddLogModal && (
        <div className="modal-overlay" onClick={() => setShowAddLogModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Time Log</h3>
              <button className="close-btn" onClick={() => setShowAddLogModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Activity Type</label>
                <select
                  value={manualLog.activityType}
                  onChange={(e) => {
                    const newType = e.target.value as 'goal' | 'task' | 'habit' | 'meditation';
                    setManualLog(prev => ({ 
                      ...prev, 
                      activityType: newType,
                      activityId: '',
                      activityName: ''
                    }));
                    setActivitySearchQuery('');
                    setShowActivityDropdown(false);
                    setHighlightedIndex(-1);
                  }}
                >
                  <option value="goal">Goal</option>
                  <option value="task">Task</option>
                  <option value="habit">Habit</option>
                  <option value="meditation">Meditation</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Select Existing Activity</label>
                {loadingActivities ? (
                  <div className="loading-text">Loading activities...</div>
                ) : (
                  <div className="autocomplete-wrapper">
                    <input
                      type="text"
                      className="autocomplete-input"
                      value={activitySearchQuery}
                      onChange={(e) => handleActivityInputChange(e.target.value)}
                      onFocus={handleActivityInputFocus}
                      onBlur={handleActivityInputBlur}
                      onKeyDown={handleKeyDown}
                      placeholder="Type to search activities..."
                    />
                    {showActivityDropdown && getFilteredActivities().length > 0 && (
                      <div className="autocomplete-dropdown">
                        {getFilteredActivities().map((activity, index) => {
                          const activityId = activity.id.toString();
                          const activityName = manualLog.activityType === 'goal' 
                            ? activity.title 
                            : activity.name || activity.title;
                          return (
                            <div
                              key={activityId}
                              className={`autocomplete-option ${index === highlightedIndex ? 'highlighted' : ''}`}
                              onClick={() => handleActivitySelect(activityId, activityName)}
                              onMouseEnter={() => setHighlightedIndex(index)}
                            >
                              {activityName}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {showActivityDropdown && activitySearchQuery.trim() && getFilteredActivities().length === 0 && (
                      <div className="autocomplete-dropdown">
                        <div className="autocomplete-no-results">No activities found</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="form-group">
                <label>Activity Name</label>
                <input
                  type="text"
                  value={manualLog.activityName}
                  onChange={(e) => setManualLog(prev => ({ ...prev, activityName: e.target.value }))}
                  placeholder="Enter activity name (or select from above)"
                />
              </div>
              
              <div className="form-group">
                <label>Duration (minutes)</label>
                <input
                  type="number"
                  value={manualLog.duration}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      setManualLog(prev => ({ ...prev, duration: '' }));
                    } else {
                      const numValue = parseInt(value, 10);
                      if (!isNaN(numValue)) {
                        setManualLog(prev => ({ ...prev, duration: numValue }));
                      }
                    }
                  }}
                  min="1"
                  placeholder="Enter duration in minutes"
                />
              </div>
              
              <div className="form-group">
                <label>Date</label>
                <input
                  type="date"
                  value={manualLog.date}
                  onChange={(e) => setManualLog(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
              
              <div className="form-group">
                <label>Description (optional)</label>
                <textarea
                  value={manualLog.description}
                  onChange={(e) => setManualLog(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Enter description"
                  rows={3}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="cancel-btn"
                onClick={() => setShowAddLogModal(false)}
              >
                Cancel
              </button>
              <button 
                className="save-btn"
                onClick={addManualLog}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Log'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Logs;
